import { useState, type FormEvent } from "react";
import { Link, useLocation, useSearchParams } from "wouter";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { Field, TextInput } from "../components/ui";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export function RegisterPage() {
  const { register } = useAuth();
  const { t } = useT();
  const [, navigate] = useLocation();
  const [params] = useSearchParams();
  const next = params.get("next") ?? "/";
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (password.length < 8) {
      setError(t("register.tooShort"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await register(email.trim(), password, displayName.trim());
      navigate(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("register.failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-sm px-4 py-10 sm:py-16">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{t("register.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <Field label={t("auth.email")}>
              <TextInput type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field label={t("register.displayName")}>
              <TextInput required value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </Field>
            <Field label={t("register.passwordMin")}>
              <TextInput
                type="password"
                required
                minLength={8}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? t("register.busy") : t("register.submit")}
            </Button>
          </form>
          <p className="mt-6 text-sm text-muted-foreground">
            {t("register.haveAccount")}{" "}
            <Link
              href={`/login?next=${encodeURIComponent(next)}`}
              className="font-medium text-primary transition-colors hover:underline"
            >
              {t("common.logIn")}
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
